// Account.tsx — "/account" (hygiene-cycle spec §C), linked from the header UserMenu above
// Billing. Two sections: Profile (display name via better-auth `updateUser`; email shown
// read-only with its sign-in method) and Password (current/new/confirm via better-auth
// `changePassword`) — same plain bordered-section idiom as BotSettings, not the centered
// auth-page Card layout, since this lives inside the normal app chrome (Protected/Billing
// style). No avatar upload (Johnny's choice) — the initials avatar stays as-is.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@repo/ui/components/button';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { authClient, useSession } from '../lib/auth-client';
import { useT } from '../locales/context';
import type { Catalog } from '../locales/en';

interface AuthAccountSummary {
  providerId: string;
}

/** "Email & password" / "Google" / a capitalized fallback for anything else — derived from
 * `authClient.listAccounts()` (the only client-exposed signal for which credential types
 * exist on this user), never guessed from the session alone. */
function signInMethodLabel(accounts: AuthAccountSummary[] | undefined, t: Catalog): string | null {
  if (!accounts) return null;
  if (accounts.some((a) => a.providerId === 'credential')) return t.account.emailPasswordLabel;
  const other = accounts[0]?.providerId;
  if (!other) return null;
  if (other === 'google') return t.account.googleLabel;
  return other.charAt(0).toUpperCase() + other.slice(1);
}

/** Sized to the real Profile/Password sections below — h1 + two bordered field blocks
 * (handoff Shell Anatomy §06). */
function AccountSkeleton() {
  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <Skeleton className="h-8 w-32" />
      {[0, 1].map((i) => (
        <div key={i} className="flex flex-col gap-4 rounded-xl border p-4">
          <SkeletonText className="w-20" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ))}
    </div>
  );
}

export function Account() {
  const t = useT();
  const { data: session, isPending } = useSession();

  // Hidden entirely for Google-only accounts (spec §C) — default to hiding whenever this is
  // uncertain (still loading, or the call failed) rather than ever showing a password form
  // that can only fail. `listAccounts` is the client method for better-auth's core
  // `GET /list-accounts` endpoint (verified against the installed package's own types).
  const accountsQuery = useQuery({
    queryKey: ['auth', 'accounts'],
    queryFn: async () => {
      const { data, error } = await authClient.listAccounts();
      if (error) throw new Error(error.message ?? t.account.loadSignInMethodsError);
      return data as AuthAccountSummary[];
    },
    enabled: !!session,
  });

  const hasPasswordCredential = accountsQuery.data?.some((a) => a.providerId === 'credential') ?? false;

  if (isPending) return <AccountSkeleton />;
  if (!session) return null; // Protected already redirects unauthenticated users away.

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <h1 className="font-display text-2xl font-bold">{t.account.pageTitle}</h1>

      {/* `key` — lazy `useState` initializers below derive from the session's name/email
          exactly once per user, the same pattern BotSettings uses for `bot.id`. */}
      <ProfileSection
        key={session.user.id}
        userId={session.user.id}
        name={session.user.name}
        email={session.user.email}
        signInMethod={signInMethodLabel(accountsQuery.data, t)}
        t={t}
      />

      {hasPasswordCredential && <PasswordSection t={t} />}
    </div>
  );
}

function ProfileSection({
  name: initialName,
  email,
  signInMethod,
  t,
}: {
  userId: string;
  name: string;
  email: string;
  signInMethod: string | null;
  t: Catalog;
}) {
  const { refetch: refetchSession } = useSession();
  const [name, setName] = useState(initialName);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);
    const { error: updateError } = await authClient.updateUser({ name: name.trim() });
    setIsSaving(false);
    if (updateError) {
      setError(updateError.message ?? t.account.saveNameErrorFallback);
      return;
    }
    // The header's UserMenu reads the same session atom — refetch so any name-derived UI
    // picks up the change immediately rather than on the next natural revalidation.
    await refetchSession();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 rounded-xl border p-4"
    >
      <h2 className="text-sm font-semibold">{t.account.profileHeading}</h2>

      <div className="flex flex-col gap-2">
        <Label htmlFor="display-name">{t.account.displayNameLabel}</Label>
        <Input
          id="display-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t.account.namePlaceholder}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label>{t.account.emailLabel}</Label>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{email}</span>
          {signInMethod && (
            <span className="rounded-full border px-2 py-0.5 text-[11.5px] font-medium text-muted-foreground">
              {signInMethod}
            </span>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={isSaving || name.trim().length === 0}>
          {isSaving ? t.account.savingCta : t.account.saveCta}
        </Button>
        {saved && <span className="text-sm text-muted-foreground">{t.account.savedLabel}</span>}
      </div>
    </form>
  );
}

function PasswordSection({ t }: { t: Catalog }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError(t.account.passwordMismatchError);
      return;
    }
    setIsSaving(true);
    const { error: changeError } = await authClient.changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: false,
    });
    setIsSaving(false);
    if (changeError) {
      setError(changeError.message ?? t.account.changePasswordErrorFallback);
      return;
    }
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 rounded-xl border p-4">
      <h2 className="text-sm font-semibold">{t.account.passwordHeading}</h2>

      <div className="flex flex-col gap-2">
        <Label htmlFor="current-password">{t.account.currentPasswordLabel}</Label>
        <Input
          id="current-password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="new-password">{t.account.newPasswordLabel}</Label>
        <Input
          id="new-password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="confirm-password">{t.account.confirmNewPasswordLabel}</Label>
        <Input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={isSaving}>
          {isSaving ? t.account.savingCta : t.account.changePasswordCta}
        </Button>
        {saved && <span className="text-sm text-muted-foreground">{t.account.passwordChangedLabel}</span>}
      </div>
    </form>
  );
}
